import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  HelpCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useApp } from "../domain/AppContext";
import type { LoginMode } from "../domain/types";
import { PublicMedalistSlider } from "../components/PublicMedalistSlider";
import { PublicHighlightsDialog } from "../components/PublicHighlightsDialog";
import { Button, Dialog, Field, InlineMessage } from "../components/ui";
import { listPublicMedalists, type PublicMedalist } from "../services/publicHighlights";

export function LoginPage() {
  const { profile, authLoading, login, requestRecovery } = useApp();
  const [identity, setIdentity] = useState("");
  const [loginMode, setLoginMode] = useState<LoginMode>("collaborator");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepConnected, setKeepConnected] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [highlightsOpen, setHighlightsOpen] = useState(false);
  const [medalists, setMedalists] = useState<PublicMedalist[]>([]);
  const [medalistsLoading, setMedalistsLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add("login-body");
    return () => document.body.classList.remove("login-body");
  }, []);

  useEffect(() => {
    let active = true;
    void listPublicMedalists(2026).then((items) => {
      if (!active) return;
      setMedalists(items);
      setMedalistsLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!authLoading && profile) return <Navigate to="/dashboard" replace />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(identity, password, loginMode, keepConnected);
    setLoading(false);
    if (!result.ok) setError(result.error.message);
  };

  const submitRecovery = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    const result = await requestRecovery(identity, loginMode);
    setLoading(false);
    if (result.ok) setRecoveryMessage(result.data.message);
    else setError(result.error.message);
  };

  const selectMode = (mode: LoginMode) => {
    setLoginMode(mode);
    setIdentity("");
    setPassword("");
    setError("");
    setRecoveryMessage("");
  };

  const roleLabel = loginMode === "student" ? "estudante" : "professor";
  const identityLabel =
    loginMode === "student" ? "RA do estudante" : "E-mail institucional";
  const identityPlaceholder =
    loginMode === "student" ? "Digite os 8 dígitos do RA" : "nome@escola.br";

  return (
    <main id="conteudo-principal" className="login-page">
      <section className="login-institutional">
        <div className="login-brand">
          <img src="./brand/icon-05-sangria-2048.png" alt="" />
          <div>
            <strong>Oficina Sapiens</strong>
            <span>Colégios Univap</span>
          </div>
        </div>
        <div className="login-public-content">
          <aside className="site-modernization-notice" aria-label="Aviso sobre inscrições">
            <strong>Estamos modernizando a Oficina Sapiens</strong>
            <p>
              As inscrições deste ano foram encerradas. Voltaremos no próximo
              ano com novas inscrições e possibilidades.
            </p>
          </aside>
          <section className="public-medalists" aria-labelledby="public-medalists-title">
            <p className="eyebrow light">Orgulho que inspira</p>
            <h1 id="public-medalists-title">Medalhistas em destaque de 2026</h1>
            <p className="public-medalists-intro">
              Um espaço para celebrar as conquistas publicadas neste ano.
            </p>
            <PublicMedalistSlider medalists={medalists} loading={medalistsLoading} />
            <button
              type="button"
              className="public-medalists-expand"
              aria-haspopup="dialog"
              disabled={medalistsLoading}
              onClick={() => setHighlightsOpen(true)}
            >
              <span>{medalistsLoading ? "Preparando mural..." : "Explorar mural de conquistas"}</span>
              <ArrowUpRight size={15} aria-hidden="true" />
            </button>
          </section>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-form-wrap">
          {recoveryMode ? (
            <>
              <button
                type="button"
                className="back-link"
                onClick={() => {
                  setRecoveryMode(false);
                  setError("");
                  setRecoveryMessage("");
                }}
              >
                <ArrowLeft size={17} /> Voltar para entrar
              </button>
              <p className="eyebrow">Recuperação de acesso</p>
              <h2>Solicitar redefinição de senha</h2>
              <p className="form-intro">
                {loginMode === "student"
                  ? "Informe seu RA. A solicitação será encaminhada ao Gestor de Sistema, sem revelar se o cadastro existe."
                  : "Informe seu e-mail. A solicitação será encaminhada ao Gestor de Sistema, sem revelar se o cadastro existe."}
              </p>
              {recoveryMessage ? (
                <InlineMessage type="success" title="Solicitação recebida">
                  {recoveryMessage}
                </InlineMessage>
              ) : (
                <form onSubmit={submitRecovery}>
                  <Field label={identityLabel} error={error}>
                    <input
                      type={loginMode === "student" ? "text" : "email"}
                      inputMode={loginMode === "student" ? "numeric" : "email"}
                      value={identity}
                      onChange={(event) =>
                        setIdentity(
                          loginMode === "student"
                            ? event.target.value.replace(/\D/g, "").slice(0, 8)
                            : event.target.value,
                        )
                      }
                      placeholder={identityPlaceholder}
                      autoComplete="username"
                    />
                  </Field>
                  <Button type="submit" loading={loading}>
                    Solicitar redefinição
                  </Button>
                </form>
              )}
              <div className="human-support">
                <strong>Prazo de atendimento</strong>
                <p>
                  O Gestor de Sistema poderá redefinir a senha em até 3 dias úteis.
                  Depois, entre com inicial@123 e crie novamente sua senha pessoal.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mobile-login-brand">
                <img src="./brand/icon-05-sangria-2048.png" alt="" />
                <div>
                  <strong>Oficina Sapiens</strong>
                  <span>Olimpíadas acadêmicas</span>
                </div>
              </div>
              <p className="eyebrow">Acesso à plataforma</p>
              <h2>Entrar na Oficina Sapiens</h2>
              <p className="form-intro">
                Escolha o perfil, informe seu identificador e use sua senha pessoal.
              </p>
              <div
                className="login-role-selector"
                role="group"
                aria-label="Tipo de usuário"
              >
                {(
                  [
                    ["student", "Estudante", "RA e senha"],
                    ["collaborator", "Professor", "E-mail e senha"],
                  ] as const
                ).map(([role, title, hint]) => (
                  <button
                    key={role}
                    type="button"
                    className={loginMode === role ? "active" : ""}
                    aria-pressed={loginMode === role}
                    onClick={() => selectMode(role)}
                  >
                    <strong>{title}</strong>
                    <span>{hint}</span>
                  </button>
                ))}
              </div>
              <form onSubmit={submit}>
                <Field label={identityLabel}>
                  <input
                    type={loginMode === "student" ? "text" : "email"}
                    inputMode={loginMode === "student" ? "numeric" : "email"}
                    value={identity}
                    onChange={(event) =>
                      setIdentity(
                        loginMode === "student"
                          ? event.target.value.replace(/\D/g, "").slice(0, 8)
                          : event.target.value,
                      )
                    }
                    placeholder={identityPlaceholder}
                    autoComplete="username"
                  />
                </Field>
                <Field label="Senha">
                  <div className="password-field">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Digite sua senha"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((current) => !current)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      <span>{showPassword ? "Ocultar" : "Mostrar"}</span>
                    </button>
                  </div>
                </Field>
                <div className="login-options">
                  <label>
                    <input
                      type="checkbox"
                      checked={keepConnected}
                      onChange={(event) =>
                        setKeepConnected(event.target.checked)
                      }
                    />
                    <span>
                      <Check size={13} /> Manter conectado
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setRecoveryMode(true);
                      setError("");
                    }}
                  >
                    Esqueci minha senha
                  </button>
                </div>
                {error ? <InlineMessage type="error" title={error} /> : null}
                <Button type="submit" loading={loading}>
                  Entrar como {roleLabel}
                </Button>
              </form>
              <button
                type="button"
                className="login-tutorial-trigger"
                onClick={() => setTutorialOpen(true)}
              >
                <HelpCircle size={18} />
                <span>
                  <strong>Como fazer login?</strong>
                  <small>
                    Veja a senha inicial, a senha temporária e o passo a passo de acesso.
                  </small>
                </span>
              </button>
            </>
          )}
        </div>
      </section>
      <PublicHighlightsDialog open={highlightsOpen} onClose={() => setHighlightsOpen(false)} medalists={medalists} loading={medalistsLoading} />
      <Dialog
        open={tutorialOpen}
        title={`Como fazer login como ${roleLabel}`}
        description="Passo a passo de acesso ao Oficina Sapiens"
        onClose={() => setTutorialOpen(false)}
        actions={
          <Button onClick={() => setTutorialOpen(false)}>Entendi</Button>
        }
      >
        <ol className="login-tutorial-steps">
          <li>
            <span>1</span>
            <div>
              <strong>Escolha o perfil correto</strong>
              <p>
                Selecione {loginMode === "student" ? "Estudante" : "Professor"}. O sistema valida o tipo de conta antes de
                liberar o acesso.
              </p>
            </div>
          </li>
          <li>
            <span>2</span>
            <div>
              <strong>
                {loginMode === "student" ? "Informe seu RA" : "Informe seu e-mail institucional"}
              </strong>
              <p>
                {loginMode === "student"
                  ? "Alunos usam sempre o RA de 8 dígitos."
                  : "Professores e o Gestor de Sistema usam sempre o e-mail cadastrado, independentemente da hierarquia."}
              </p>
            </div>
          </li>
          <li>
            <span>3</span>
            <div>
              <strong>Digite sua senha</strong>
              <p>
                No primeiro acesso, use a senha padrão <strong>inicial@123</strong>.
                Depois de criar sua senha pessoal, use sempre essa senha. Se a
                conta tiver sido redefinida, use novamente <strong>inicial@123</strong>
                e crie outra senha ao entrar.
              </p>
            </div>
          </li>
          <li>
            <span>4</span>
            <div>
              <strong>Conclua o acesso</strong>
              <p>
                Clique em “Entrar como {roleLabel}”. Se não conseguir, use
                “Esqueci minha senha” ou procure a coordenação.
              </p>
            </div>
          </li>
        </ol>
        <InlineMessage title="Cuide do seu acesso">
          A senha padrão <strong>inicial@123</strong> vale somente no primeiro
          acesso ou após uma redefinição. Não compartilhe sua senha pessoal.
        </InlineMessage>
      </Dialog>
    </main>
  );
}
